import map from "lodash/map";
import {
  AddAttachmentButton,
  AttachmentsSelector
} from "PFApp/components/activity_form/form_selectors/attachments_selector";
import { FIELD_NAME_KEY, FIELD_UNIQUE_ID_KEY } from "PFApp/constants/activity_form/activity_form_field_keys";
import {
  useMultiActivityFormStore,
  useMultiActivityFormStoreActions
} from "PFApp/stores/activity_form/multi_roles_form";
import { RoleFormKey } from "PFApp/types/activity_form/activity_form_schema";
import { Inline, Stack } from "PFComponents/containers";
import { InputFocusProps } from "PFComponents/table/interactive/use_cell_input_focus";
import { Typography } from "PFComponents/typography";
import { Attachment, Id } from "PFCore/types";
import { RefObject, useState } from "react";
import { useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { SidePanelCellFormSection } from "../side_panel_cell_form_section";
import css from "./attachments_cell.module.scss";

// TODO: [SP-7181] Fetch Activity Attachments for edit
type AttachmentsCellProps = {
  onChange: (attachments: Id[]) => void;
  roleFormKey: RoleFormKey;
  disabled?: boolean;
  error?: string;
  inputFocusProps?: InputFocusProps;
  buttonRef?: RefObject<HTMLButtonElement>;
};

export const AttachmentsCell = ({
  onChange,
  roleFormKey,
  disabled,
  error,
  inputFocusProps,
  buttonRef
}: AttachmentsCellProps) => {
  const { t: tAttachments } = useTranslation("activities", { keyPrefix: "form.sections.attachments" });
  const { t: tBlueprints } = useTranslation("activities", { keyPrefix: "engagementBlueprint" });
  const { setRoleData } = useMultiActivityFormStoreActions();

  const uniqueId = useWatch({ name: `${roleFormKey}.${FIELD_UNIQUE_ID_KEY}` });
  const roleAttachments = useMultiActivityFormStore((store) => store.roles[uniqueId]?.attachments);
  const [pendingAttachments, setPendingAttachments] = useState<Attachment[]>([]);
  const localAttachments = [...roleAttachments, ...pendingAttachments];
  const roleName = useWatch({ name: `${roleFormKey}.${FIELD_NAME_KEY}` });

  const handleAttachmentsChange = (newAttachments: Attachment[]) => {
    onChange(map(newAttachments, "id"));
    setRoleData(uniqueId, { attachments: newAttachments });
  };

  const handleUploadStart = (pendingAttachment: Attachment) => {
    setPendingAttachments((prev) => [...prev, pendingAttachment]);
  };

  const handleUploadSuccess = (attachment: Attachment, pendingAttachment: Attachment) => {
    setPendingAttachments((prev) => prev.filter(({ id }) => id !== pendingAttachment.id));
    const updatedAttachments = [...roleAttachments, attachment];
    onChange(map(updatedAttachments, "id"));
    setRoleData(uniqueId, { attachments: updatedAttachments });
  };

  const handleUploadError = (pendingAttachment: Attachment) => {
    setPendingAttachments((prev) => prev.filter(({ id }) => id !== pendingAttachment.id));
  };

  const displayText = tAttachments("attachmentCount", { count: localAttachments.length });

  return (
    <SidePanelCellFormSection
      label={localAttachments.length !== 0 ? displayText : tAttachments("placeholderShort")}
      sidePanelTitle={tAttachments("title")}
      disabled={disabled}
      error={error}
      editAriaLabel={tBlueprints("editAttachmentsInRole", {
        roleName: roleName || tBlueprints("emptyRoleName")
      })}
      inputFocusProps={inputFocusProps}
      buttonRef={buttonRef}
    >
      <Stack>
        <Inline alignItems="center" spread className={css.counter}>
          <Typography variant="bodyRegular">{displayText}</Typography>
          <AddAttachmentButton
            onUploadStart={handleUploadStart}
            onUploadSuccess={handleUploadSuccess}
            onUploadError={handleUploadError}
            disabled={disabled}
          />
        </Inline>
        <AttachmentsSelector
          attachments={localAttachments}
          onChange={handleAttachmentsChange}
          disabled={disabled}
        />
      </Stack>
    </SidePanelCellFormSection>
  );
};
